// src/components/BadgesRow.tsx
import React, { useEffect } from 'react';
import './BadgesRow.css';

// Badge image imports (stored in src/assets/)
import FirstImg from '../assets/first.png';
import FiveImg from '../assets/five.png';
import TenImg from '../assets/ten.png';
import TwentyImg from '../assets/twenty.png';
import ThirtyImg from '../assets/thirty.png';
import FiftyImg from '../assets/fifty.png';
import Mile100Img from '../assets/100mile.png';
import Mile250Img from '../assets/250mile.png';
import Mile500Img from '../assets/500mile.png';
import Mile1000Img from '../assets/1000mile.png';
import Mile5000Img from '../assets/5000mile.png';

interface Badge {
  id: string;
  title: string;
  description: string;
  image: string;
  earned: boolean;
}

interface Props {
  completedChallenges: number; // Must be challenges with percentage >= 100
  totalDistanceKm: number;
  isKilometers: boolean;
}

const MILE_IN_KM = 1.60934;

export default function BadgesRow({
  completedChallenges,
  totalDistanceKm,
  isKilometers,
}: Props) {
  // Convert totalDistanceKm to miles (for distance badges)
  const miles = totalDistanceKm / MILE_IN_KM;

  // Debug: verify props are arriving correctly
  useEffect(() => {
    console.log('[BadgesRow] props →', {
      completedChallenges,
      totalDistanceKm,
      isKilometers,
      miles,
    });
  }, [completedChallenges, totalDistanceKm, isKilometers, miles]);

  // Build your full list of badges, marking earned based on thresholds
  const badgeList: Badge[] = [
    {
      id: 'first',
      title: 'First Challenge',
      description: 'Completed your first challenge!',
      image: FirstImg,
      earned: completedChallenges >= 1,
    },
    {
      id: 'five',
      title: 'Five Challenges',
      description: 'Completed 5 challenges!',
      image: FiveImg,
      earned: completedChallenges >= 5,
    },
    {
      id: 'ten',
      title: 'Ten Challenges',
      description: 'Completed 10 challenges!',
      image: TenImg,
      earned: completedChallenges >= 10,
    },
    {
      id: 'twenty',
      title: 'Twenty Challenges',
      description: 'Completed 20 challenges!',
      image: TwentyImg,
      earned: completedChallenges >= 20,
    },
    {
      id: 'thirty',
      title: 'Thirty Challenges',
      description: 'Completed 30 challenges!',
      image: ThirtyImg,
      earned: completedChallenges >= 30,
    },
    {
      id: 'fifty',
      title: 'Fifty Challenges',
      description: 'Completed 50 challenges!',
      image: FiftyImg,
      earned: completedChallenges >= 50,
    },
    {
      id: 'mile100',
      title: '100 Mile Badge',
      description: 'Traveled 100 miles!',
      image: Mile100Img,
      earned: miles >= 100,
    },
    {
      id: 'mile250',
      title: '250 Mile Badge',
      description: 'Traveled 250 miles!',
      image: Mile250Img,
      earned: miles >= 250,
    },
    {
      id: 'mile500',
      title: '500 Mile Badge',
      description: 'Traveled 500 miles!',
      image: Mile500Img,
      earned: miles >= 500,
    },
    {
      id: 'mile1000',
      title: '1000 Mile Badge',
      description: 'Traveled 1000 miles!',
      image: Mile1000Img,
      earned: miles >= 1000,
    },
    {
      id: 'mile5000',
      title: '5000 Mile Badge',
      description: 'Traveled 5000 miles!',
      image: Mile5000Img,
      earned: miles >= 5000,
    },
  ];

  // Filter out only those that are earned
  const earnedBadges = badgeList.filter((badge) => badge.earned);

  // Debug: log exactly which badges are earned
  useEffect(() => {
    console.log('[BadgesRow] earnedBadges →', earnedBadges.map((b) => b.id));
  }, [earnedBadges]);

  // If absolutely none are earned, return null (hide component)
  if (earnedBadges.length === 0) {
    return null;
  }

  return (
    <div className="badges-row-container">
      <h3 className="badges-title">Earned Badges</h3>
      <div className="badges-scroll">
        {earnedBadges.map((badge) => (
          <div className="badge-card" key={badge.id}>
            <img
              src={badge.image}
              alt={badge.title}
              className="badge-image"
            />
            <div className="badge-title">{badge.title}</div>
            <div className="badge-description">{badge.description}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
